import {
    MixModel,
    ProjectDataIndex,
    ProjectSceneScriptAndHtmlModel
} from '../../api/types.ts';
import React, {useEffect, useRef, useState} from 'react';
import useFetchProjectSceneScriptAndHtml from '../../api/scene/useFetchProjectSceneScriptAndHtml.ts';
import {useMutation, useQuery} from 'react-query';
import useUpdateSceneScriptAndHtml from '../../api/scene/useUpdateSceneScriptAndHtml.ts';
import {toast} from 'react-toastify';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import {ReactStateSetter} from '../../types.ts';
import {Alert, Button, Spinner} from 'reactstrap';
import CodeEditor from '../shared/CodeEditor.tsx';
import VibeCodeModal from './VibeCodeModal.tsx';
import VibeCodeHistoryModal from './VibeCodeHistoryModal.tsx';
import Icon from '../shared/Icon.tsx';
import {DownloadIcon, HistoryIcon, SparklesIcon} from 'lucide-react';

interface VibeCodeToolsProps {
    project: MixModel;
    dataIndex: ProjectDataIndex;
    setSyncToken: ReactStateSetter<string>;
    autoOpenVibe?: boolean;
    // With autoOpenVibe on a copy: translate it into this language.
    autoVibeLanguage?: string | null;
    onVibeAutoOpened?: () => void;
}

const VibeCodeTools: React.FC<VibeCodeToolsProps> = ({
    project,
    dataIndex,
    setSyncToken,
    autoOpenVibe,
    autoVibeLanguage,
    onVibeAutoOpened
}) => {
    const {id} = project;
    const projectId = id!;

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [historyIsOpen, setHistoryIsOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'vibe' | 'edit'>('vibe');
    const [scene, setScene] = useState<ProjectSceneScriptAndHtmlModel | null>(
        null
    );

    // Snapshot of the tab's HTML from before the current Vibe session so a
    // "Discard & close" can restore it after the assistant has auto-applied.
    const baselineRef = useRef<{captured: boolean; html: string}>({
        captured: false,
        html: ''
    });
    const fetchProjectSceneScriptAndHtml = useFetchProjectSceneScriptAndHtml(
        projectId,
        dataIndex
    );
    const {
        data: initialProjectSceneScriptAndHtml,
        isError: sceneIsError,
        isLoading: sceneIsLoading
    } = useQuery(
        ['projectscenescriptandhtml', projectId, dataIndex],
        fetchProjectSceneScriptAndHtml
    );

    useEffect(() => {
        if (initialProjectSceneScriptAndHtml) {
            setScene(initialProjectSceneScriptAndHtml);
            setSyncToken(initialProjectSceneScriptAndHtml.syncToken);
        }
    }, [initialProjectSceneScriptAndHtml, setScene, setSyncToken]);

    // A tab that still holds the placeholder has nothing to edit, so open the
    // Vibe assistant and its starting templates rather than the placeholder
    // page. Once per visit: closing it leaves the person with the editor.
    const openedForPlaceholderRef = useRef(false);

    useEffect(() => {
        if (
            initialProjectSceneScriptAndHtml?.isPlaceholder &&
            !openedForPlaceholderRef.current
        ) {
            openedForPlaceholderRef.current = true;
            setModalMode('vibe');
            setModalIsOpen(true);
        }
    }, [initialProjectSceneScriptAndHtml]);

    // When a Vibe Code tab was just created, or a game was just remixed, jump
    // straight into the Vibe assistant: a fresh prompt for an empty tab, or
    // "what would you like to change?" over the copied code. Waits for the code
    // to know which. onVibeAutoOpened clears the parent flag so it fires once.
    const [askWhatToChange, setAskWhatToChange] = useState(false);
    // A copy made from the translator translates itself into this language.
    const [translateInto, setTranslateInto] = useState<string | null>(null);

    useEffect(() => {
        if (autoOpenVibe && initialProjectSceneScriptAndHtml) {
            const hasCode = !initialProjectSceneScriptAndHtml.isPlaceholder;
            setModalMode(hasCode ? 'edit' : 'vibe');
            setAskWhatToChange(hasCode);
            setTranslateInto(hasCode ? autoVibeLanguage ?? null : null);
            setModalIsOpen(true);
            onVibeAutoOpened?.();
        }
    }, [
        autoOpenVibe,
        autoVibeLanguage,
        initialProjectSceneScriptAndHtml,
        onVibeAutoOpened
    ]);

    // Each time the modal opens, arm a fresh baseline capture for discard.
    useEffect(() => {
        if (modalIsOpen) {
            baselineRef.current = {captured: false, html: ''};
        }
    }, [modalIsOpen]);

    const updateSceneScriptAndHtml = useUpdateSceneScriptAndHtml(
        projectId,
        dataIndex
    );
    const mutation = useMutation(updateSceneScriptAndHtml);
    const [backendError, setBackendError] = useState<string | null>(null);

    const applySceneUpdate = (
        updatedScene?: ProjectSceneScriptAndHtmlModel
    ) => {
        setBackendError(null);

        mutation.mutate(updatedScene ?? scene!, {
            onSuccess: idResult => {
                toast.success('Code updated');
                if (idResult) {
                    setSyncToken(idResult.id);
                }
            },
            onError: (error: unknown) => {
                setBackendError(unknownErrorToString(error));
            }
        });
    };

    const handleApplyHtml = (html: string) => {
        // Remember what was here before the first change of this session so we
        // can offer to roll back if the user discards.
        if (!baselineRef.current.captured) {
            baselineRef.current = {
                captured: true,
                html: scene?.sceneHtml ?? ''
            };
        }

        const updatedScene = {
            ...scene!,
            sceneHtml: html
        };
        setScene(updatedScene);
        applySceneUpdate(updatedScene);
    };

    const discardVibeChanges = () => {
        const baseline = baselineRef.current;

        // Only restore if the assistant actually changed the content.
        if (baseline.captured && scene && baseline.html !== scene.sceneHtml) {
            const revertedScene = {...scene, sceneHtml: baseline.html};
            setScene(revertedScene);
            applySceneUpdate(revertedScene);
        }

        setModalIsOpen(false);
    };

    const downloadHtml = () => {
        if (!scene?.sceneHtml) {
            toast.error('No HTML content to download');
            return;
        }

        applySceneUpdate();

        const blob = new Blob([scene.sceneHtml], {type: 'text/html'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `vibe.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const openVibeModal = () => {
        setModalMode('vibe');
        setAskWhatToChange(false);
        setTranslateInto(null);
        setModalIsOpen(true);
    };

    const openEditModal = () => {
        setModalMode('edit');
        setAskWhatToChange(false);
        setTranslateInto(null);
        setModalIsOpen(true);
    };

    if (sceneIsLoading) {
        return (
            <div className="spinner-full">
                <Spinner color="primary" />
            </div>
        );
    }

    if (sceneIsError) {
        return <>Error loading HTML code</>;
    }

    return (
        <>
            <VibeCodeModal
                isOpen={modalIsOpen}
                mix={project}
                dataIndex={dataIndex}
                mode={modalMode}
                existingHtml={
                    modalMode === 'edit' ? scene?.sceneHtml : undefined
                }
                askWhatToChange={askWhatToChange}
                translateInto={translateInto}
                onClose={() => setModalIsOpen(false)}
                onDiscard={discardVibeChanges}
                onApplyHtml={handleApplyHtml}
            />
            <VibeCodeHistoryModal
                isOpen={historyIsOpen}
                projectId={projectId}
                dataIndex={dataIndex}
                onClose={() => setHistoryIsOpen(false)}
            />
            <CodeEditor
                value={scene?.sceneHtml ?? ''}
                onChange={code =>
                    setScene(preState => ({
                        ...preState!,
                        sceneHtml: code
                    }))
                }
                language="html"
                height={'100%'}
            />
            <div className="actions">
                <Button
                    onClick={downloadHtml}
                    color="tertiary"
                    className="me-auto"
                    title="Download the HTML file"
                >
                    <Icon icon={DownloadIcon} />
                    Download
                </Button>
                <Button
                    onClick={() => setHistoryIsOpen(true)}
                    color="tertiary"
                    title="Earlier prompts and what they produced"
                >
                    <Icon icon={HistoryIcon} />
                    History
                </Button>
                <Button onClick={openVibeModal} color="secondary">
                    <Icon icon={SparklesIcon} />
                    Vibe
                </Button>
                <Button onClick={openEditModal} color="secondary">
                    Edit
                </Button>
                <Button
                    onClick={() => {
                        applySceneUpdate();
                    }}
                    color="primary"
                >
                    Apply
                </Button>
            </div>
            {backendError && <Alert color="danger mt-3">{backendError}</Alert>}
        </>
    );
};

export default VibeCodeTools;
